/**
 * Manos pricing model - canonical source.
 *
 * Locked in 2026-04-16. Supersedes the earlier starter/pro/business/
 * enterprise tier model. Aligned with the approved pricing spec in
 * HouseProud/docs/design/product-spec.md and researched in
 * docs/pricing-and-unit-economics.md (read that doc for the full
 * rationale, unit economics, and competitive positioning).
 *
 * Key differences from the earlier model:
 *   - Seat-based, not feature-gated (every paid tier gets every feature)
 *   - Solo / Team / Enterprise (not starter/pro/business)
 *   - Shopify-style launch funnel: TRIAL_DAYS free, then PROMO_MONTHS
 *     at 1/mo via per-currency launch coupons, then full price at
 *     month TRIAL_DAYS+PROMO_MONTHS+1
 *   - Tiered application_fee_percent: 1.0% / 0.7% / 0.5% -- novel vs
 *     every trades-CRM competitor (Jobber, Housecall Pro, ServiceTitan,
 *     Tradify, Fergus all pass Stripe's standard rate through unchanged)
 *   - Multi-region pricing (GBP, USD, CAD) with region-specific
 *     Stripe Price IDs
 *
 * Cross-boundary numeric constants live in /config:
 *   - TRIAL_DAYS, PROMO_MONTHS  -> /config/timing.ts
 *   - commission tier rates     -> /config/commissionRates.ts
 *
 * This file holds the plan SHAPE (display copy, feature lists,
 * Stripe Price IDs, region-currency mapping). Read-throughs of the
 * shared constants are re-exported below for consumer convenience.
 */

import {
  COMMISSION_TIER_DEFAULTS,
  applicationFeeForPlan as sharedApplicationFeeForPlan,
} from "../../config/commissionRates";
import {
  TRIAL_DAYS as SHARED_TRIAL_DAYS,
  PROMO_MONTHS as SHARED_PROMO_MONTHS,
} from "../../config/timing";

export type ManoPlan = "solo" | "team" | "enterprise";

/**
 * Number of free trial days granted on new profile creation.
 * Re-exported from /config/timing.ts so existing imports keep working.
 */
export const TRIAL_DAYS = SHARED_TRIAL_DAYS;

/**
 * Duration of the promotional 1-unit/mo window after the free trial
 * ends. Re-exported from /config/timing.ts.
 */
export const PROMO_MONTHS = SHARED_PROMO_MONTHS;

/**
 * Supported currencies / regions. Drives the pricing block on each plan
 * and the Stripe Price ID selection at Checkout time.
 */
export type Currency = "gbp" | "usd" | "cad";

/**
 * The Stripe Product IDs and Price IDs for each plan in each currency.
 *
 * These must be created in the Stripe Dashboard (test mode first) with
 * metadata.tier set to the matching plan key, so the webhook handler
 * can map checkout.session.completed events back to our plan enum via
 * tierFromMetadata() in POST-webhooks-stripe.ts.
 *
 * Test-mode Products and Prices created 2026-04-16 via one-shot
 * setup route. Replace with live-mode IDs before production launch.
 */
/**
 * Stripe price `lookup_key` values, NOT raw price IDs. Lookup keys are
 * stable across Stripe accounts (sandbox vs live) so the same value
 * resolves to whichever account the connected STRIPE_SECRET_KEY points
 * at. Hardcoded raw price IDs leaked test-account values into prod
 * once already; lookup keys avoid that.
 *
 * Resolved at runtime by /keep-plan via
 * stripe.prices.list({ lookup_keys, active: true }).
 */
export const STRIPE_PRICE_LOOKUP_KEYS: Record<
  "solo" | "team",
  Record<Currency, string>
> = {
  solo: {
    gbp: "solo_gbp_monthly",
    usd: "solo_usd_monthly",
    cad: "solo_cad_monthly",
  },
  team: {
    gbp: "team_gbp_monthly",
    usd: "team_usd_monthly",
    cad: "team_cad_monthly",
  },
};

export interface PlanPricing {
  /** Monthly price in the subunit's major denomination (e.g. 29 for £29). */
  monthly: number | null;
  /** Formatted display string for the UI, e.g. "£29/mo". */
  display: string;
  /**
   * Stripe price `lookup_key` (null for enterprise / unpriced tiers).
   * Resolved to a concrete price id at the /keep-plan loader boundary
   * so dev (sandbox) and prod (live) work off the same value.
   */
  stripeLookupKey: string | null;
}

export interface PlanDefinition {
  key: ManoPlan;
  displayName: string;
  /** Max users on this plan. null for enterprise (custom). */
  seats: number | null;
  /** Pricing per region. Keys are ISO alpha-2 lowercased. */
  pricing: Record<Currency, PlanPricing>;
  /**
   * Stripe Connect application_fee_percent -- what Manos takes on top
   * of Stripe's base rate on every Connect card transaction. Decimal
   * form, e.g. 0.01 = 1.0%.
   *
   * Heavy processors save by upgrading: Solo 1.0% -> Team 0.7% =
   * 0.3% reduction, break-even on £6,667/mo card volume.
   */
  applicationFeePercent: number;
  /** Bullet list of features for the billing-page card. */
  features: string[];
}

export const PLANS: Record<ManoPlan, PlanDefinition> = {
  solo: {
    key: "solo",
    displayName: "Solo",
    seats: 1,
    pricing: {
      gbp: {
        monthly: 29,
        display: "£29/mo",
        stripeLookupKey: STRIPE_PRICE_LOOKUP_KEYS.solo.gbp,
      },
      usd: {
        monthly: 29,
        display: "$29/mo",
        stripeLookupKey: STRIPE_PRICE_LOOKUP_KEYS.solo.usd,
      },
      cad: {
        monthly: 35,
        display: "CA$35/mo",
        stripeLookupKey: STRIPE_PRICE_LOOKUP_KEYS.solo.cad,
      },
    },
    applicationFeePercent: COMMISSION_TIER_DEFAULTS.solo,
    features: [
      "1 user",
      "Smart capture with voice notes",
      "Unlimited jobs, clients & invoices",
      "Calendar & scheduling",
      "Cash, bank transfer & card payments",
      "Photo documentation",
      "Follow-up reminders",
      "MTD-ready record keeping",
      "QuickBooks / Xero export",
    ],
  },
  team: {
    key: "team",
    displayName: "Team",
    seats: 3,
    pricing: {
      gbp: {
        monthly: 49,
        display: "£49/mo",
        stripeLookupKey: STRIPE_PRICE_LOOKUP_KEYS.team.gbp,
      },
      usd: {
        monthly: 59,
        display: "$59/mo",
        stripeLookupKey: STRIPE_PRICE_LOOKUP_KEYS.team.usd,
      },
      cad: {
        monthly: 69,
        display: "CA$69/mo",
        stripeLookupKey: STRIPE_PRICE_LOOKUP_KEYS.team.cad,
      },
    },
    applicationFeePercent: COMMISSION_TIER_DEFAULTS.team,
    features: [
      "Everything in Solo",
      "Up to 3 users",
      "Lower card commission (0.7% vs 1.0%)",
      "Shared client list across the team",
      "Team scheduling",
    ],
  },
  enterprise: {
    key: "enterprise",
    displayName: "Enterprise",
    seats: null,
    pricing: {
      gbp: { monthly: null, display: "Custom", stripeLookupKey: null },
      usd: { monthly: null, display: "Custom", stripeLookupKey: null },
      cad: { monthly: null, display: "Custom", stripeLookupKey: null },
    },
    applicationFeePercent: COMMISSION_TIER_DEFAULTS.enterprise,
    features: [
      "Everything in Team",
      "Unlimited users",
      "FM portfolio tiers (50 / 200 / 500+ properties)",
      "Dedicated support",
      "Custom integrations",
      "Negotiated card commission",
    ],
  },
};

/**
 * Ordered plan list for upgrade / downgrade logic. Index = tier rank.
 * Solo (0) < Team (1) < Enterprise (2).
 */
export const PLAN_ORDER: ManoPlan[] = ["solo", "team", "enterprise"];

/**
 * Return the numeric rank of a plan for comparison. Higher rank = higher
 * tier. Unknown plans default to -1 so invalid tiers sort below Solo.
 */
export function planRank(plan: string): number {
  return PLAN_ORDER.indexOf(plan as ManoPlan);
}

/**
 * Given a plan enum value, return the application_fee_percent for
 * Stripe Connect PaymentIntent creation. Re-exported from /config so
 * the same resolver is used on the server (POST-accept-quote.ts) and
 * the client (settings/subscription page display copy).
 */
export const applicationFeeForPlan = sharedApplicationFeeForPlan;

/**
 * Map a profile country code (ISO alpha-2) to the currency to use for
 * pricing display and Stripe Checkout. Defaults to GBP.
 */
export function currencyForCountry(country: string | null | undefined): Currency {
  const c = (country ?? "GB").toUpperCase();
  if (c === "US") return "usd";
  if (c === "CA") return "cad";
  return "gbp";
}

/**
 * Display string for the promotional 1-unit/mo price in the user's
 * currency, e.g. "£1/mo". Used by the trial banner and setup cards so
 * user-facing copy shows the symbol, never the ISO code.
 */
export function promoMonthlyDisplay(country: string | null | undefined): string {
  const c = currencyForCountry(country);
  if (c === "usd") return "$1/mo";
  if (c === "cad") return "CA$1/mo";
  return "£1/mo";
}

/**
 * Full-price monthly display for a given plan in the visitor's currency,
 * e.g. "£29/mo" (Solo GBP) or "CA$69/mo" (Team CAD). Paired with
 * `promoMonthlyDisplay()` so the marketing pages show both the promo
 * price and what the customer ends up paying from month 4.
 */
export function entryMonthlyDisplay(
  plan: ManoPlan,
  country: string | null | undefined,
): string {
  const c = currencyForCountry(country);
  return PLANS[plan].pricing[c].display;
}
